import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { Button } from '@ctw/shared/components/button';
import { copyToClipboard } from '@ctw/shared/components/copy-to-clipboard';
import { notify } from '@ctw/shared/components/toast';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { useResources } from '@ctw/shared/context/_internal/resource/resource-fetch';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import {
  type FetchableModelType,
  type FetchableResourceType,
  isFetchableResourceType,
} from '@ctw/shared/utils/fhir/resource-mappings';
import { tw } from '@ctw/shared/utils/tailwind';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ZusFhirResource, summarizeFhirResource } from '@zeus-health/types';
import { useCallback, useMemo } from 'react';

const SECTION_ORDER = ['Diagnoses', 'Labs & Observations', 'Medications', 'Documents'] as const;

type SectionKey = (typeof SECTION_ORDER)[number];

const RESOURCE_TYPE_TO_SECTION: Partial<Record<string, SectionKey>> = {
  Condition: 'Diagnoses',
  Observation: 'Labs & Observations',
  DiagnosticReport: 'Labs & Observations',
  MedicationStatement: 'Medications',
  DocumentReference: 'Documents',
};

function getSectionForResourceType(resourceType: string): SectionKey | 'Other' {
  return RESOURCE_TYPE_TO_SECTION[resourceType] ?? 'Other';
}

export const EvidenceSummary = ({ riskGap }: { riskGap: RiskGapModel }) => {
  const { patient } = usePatientContext();
  const includeDiagnoses = useFeatureFlag('ctw-evidence-summary-diagnoses');
  const identifiers = useMemo(() => {
    return riskGap.evidenceReferences
      .filter((ref) => isFetchableResourceType(ref.resourceType))
      .filter((ref) => includeDiagnoses || ref.resourceType !== 'Condition')
      .map((ref) => ({
        resourceId: ref.resourceId,
        resourceType: ref.resourceType as FetchableResourceType,
      }));
  }, [riskGap.evidenceReferences, includeDiagnoses]);
  const { data: resources = [], isLoading } = useResources<
    FetchableModelType<FetchableResourceType>
  >({
    identifiers,
    patient,
  });

  const referenceDisplayByResourceId = useMemo(() => {
    const map = new Map<string, string>();
    for (const ref of riskGap.evidenceReferences) {
      if (ref.display) {
        map.set(ref.resourceId, ref.display);
      }
    }
    return map;
  }, [riskGap.evidenceReferences]);

  const summaries = useMemo(
    () =>
      resources.map((model) => {
        return {
          id: model.id,
          resourceType: model.resourceType,
          text: summarizeFhirResource(
            model.resource as ZusFhirResource,
            model.resourceType === 'DocumentReference'
              ? { prefix: referenceDisplayByResourceId.get(model.id) }
              : {},
          ),
        };
      }),
    [resources, referenceDisplayByResourceId],
  );

  const groupedBySection = useMemo(() => {
    const order: Array<SectionKey | 'Other'> = [...SECTION_ORDER, 'Other'];
    const groups = new Map<
      SectionKey | 'Other',
      Array<{ id: string; resourceType: string; text: string }>
    >();
    for (const key of order) {
      groups.set(key, []);
    }
    for (const { id, resourceType, text } of summaries) {
      const section = getSectionForResourceType(resourceType);
      groups.get(section)?.push({ id, resourceType, text });
    }
    return order
      .map((section) => ({ section, items: groups.get(section) ?? [] }))
      .filter(({ items }) => items.length > 0);
  }, [summaries]);

  const copyContent = useMemo(
    () =>
      groupedBySection
        .map(
          ({ section, items }) => `${section}:\n${items.map(({ text }) => `- ${text}`).join('\n')}`,
        )
        .join('\n\n'),
    [groupedBySection],
  );
  const copyDisabled = isLoading || !copyContent;
  const handleCopy = useCallback(() => {
    if (isLoading || !copyContent) {
      return;
    }
    void copyToClipboard(copyContent, () => {
      notify({ type: 'info', title: 'Evidence summary copied to clipboard' });
    });
  }, [copyContent, isLoading]);

  if (identifiers.length === 0) {
    return null;
  }
  if (summaries.length === 0 && !isLoading) {
    return null;
  }

  return (
    <div className={tw`overflow-hidden rounded-lg border border-border bg-white shadow-sm`}>
      <div
        className={tw`flex h-14 items-center justify-between border-border border-b bg-muted/30 px-4 py-3`}
      >
        <span className={tw`font-semibold text-muted-foreground text-xs uppercase tracking-wider`}>
          Evidence Summary
        </span>
        <Tooltip content="Copy summary" id="evidence-summary-copy">
          <Button
            ariaLabel="Copy summary to clipboard"
            className={tw`h-8 w-8 rounded-md`}
            disabled={copyDisabled}
            onClick={handleCopy}
            size="sm"
            type="button"
            variant="unstyled"
          >
            <FontAwesomeIcon icon={faCopy} className={tw`h-4 w-4 text-muted-foreground`} />
          </Button>
        </Tooltip>
      </div>
      <div className={tw`px-3 py-4`}>
        {isLoading ? (
          <div className={tw`text-content-subtle text-sm`}>Loading summaries…</div>
        ) : (
          <div className={tw`whitespace-pre-wrap text-sm`}>{copyContent}</div>
        )}
      </div>
    </div>
  );
};
